import '@xyflow/react/dist/base.css';

import { useGetFlow } from '@reelplicate/api-client/flows';
import type { FlowDto, WorkspaceDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { flowGraphSchema } from '@reelplicate/flows';
import { Skeleton } from '@reelplicate/ui';
import { ReactFlowProvider } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useFullBleed } from '@/components/layout-mode';

import { createEditorStore, type EditorStore, EditorStoreContext } from './editor-store';
import { EditorTopBar } from './editor-top-bar';
import { FlowCanvas } from './flow-canvas';
import { InspectorPanel } from './inspector/inspector-panel';
import { LibraryPanel } from './library-panel';
import { RunProvider } from './run/run-provider';
import { useAutosave } from './use-autosave';

/** `/flows/:flowId` - the full-bleed flow editor. */
export function FlowEditorPage() {
  useFullBleed();
  const { flowId = '' } = useParams();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const { data: flow } = useGetFlow(workspace?.id ?? '', flowId, {
    query: { enabled: Boolean(workspace), staleTime: Infinity },
  });

  if (!workspace || !flow) {
    return (
      <div className="flex h-full flex-col" aria-busy="true">
        <div role="status" className="sr-only">
          Loading the flow
        </div>
        <div className="border-border-subtle bg-bg-surface h-15 border-b" />
        <div className="flex flex-1">
          <Skeleton shape="rectangle" className="w-72 rounded-none" />
          <div className="flex-1" />
          <Skeleton shape="rectangle" className="w-85 rounded-none" />
        </div>
      </div>
    );
  }
  return <Editor key={flow.id} workspace={workspace} flow={flow} />;
}

function Editor({ workspace, flow }: { workspace: WorkspaceDto; flow: FlowDto }) {
  const [store] = useState<EditorStore>(() =>
    createEditorStore(flowGraphSchema.parse(flow.draftGraph)),
  );
  // The query cache is updated on every save, but the editor keeps its own graph:
  // only the header (name, status, version) follows the cache.
  const { data: current = flow } = useGetFlow(workspace.id, flow.id, {
    query: { staleTime: Infinity },
  });
  const save = useAutosave({ workspaceId: workspace.id, flow, store });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        store.getState().undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        store.getState().redo();
      } else if (key === 's') {
        event.preventDefault();
        void save.flush();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [store, save]);

  return (
    <EditorStoreContext value={store}>
      <RunProvider workspaceId={workspace.id} flow={flow} beforeStart={save.flush}>
        <ReactFlowProvider>
          <div className="flex h-full min-h-0 flex-col">
            <EditorTopBar
              workspaceId={workspace.id}
              workspaceName={workspace.name}
              flow={current}
              save={save}
            />
            <div className="flex min-h-0 flex-1">
              <LibraryPanel />
              <FlowCanvas />
              <InspectorPanel workspaceId={workspace.id} />
            </div>
          </div>
        </ReactFlowProvider>
      </RunProvider>
    </EditorStoreContext>
  );
}
